/**
 * file-utils.ts - Utilitários compartilhados para arquivos e anexos
 * 
 * Funções centralizadas de ícones, cores e formatação de tamanho
 * usadas nos módulos CRM (Deals) e Tarefas.
 * 
 * Versão: 1.0.0
 * RELEASE: 2026-04-06 v1.0.0 - Extração de funções duplicadas
 * RELEASE: 2026-10-04 v1.1.0 - Ícones/cores para vídeo, áudio e compactados (anexos de Tarefas)
 */
import {
  FileText,
  Image as ImageIcon,
  FileSpreadsheet,
  File,
  FileVideo,
  FileAudio,
  FileArchive,
} from 'lucide-react';

/** Tipo base de arquivo para anexos */
export type AttachmentFileType = 'image' | 'pdf' | 'document' | 'spreadsheet' | 'other';

/**
 * Retorna o ícone Lucide apropriado baseado no tipo de arquivo
 */
export function getFileIcon(fileType: string) {
  switch (fileType) {
    case 'image':
      return ImageIcon;
    case 'pdf':
    case 'document':
      return FileText;
    case 'spreadsheet':
      return FileSpreadsheet;
    case 'video':
      return FileVideo;
    case 'audio':
      return FileAudio;
    case 'archive':
      return FileArchive;
    default:
      return File;
  }
}

/**
 * Retorna classes Tailwind de cor baseado no tipo de arquivo
 */
export function getFileColor(fileType: string): string {
  switch (fileType) {
    case 'image':
      return 'text-blue-500 bg-blue-500/10';
    case 'pdf':
      return 'text-red-500 bg-red-500/10';
    case 'document':
      return 'text-blue-600 bg-blue-600/10';
    case 'spreadsheet':
      return 'text-green-500 bg-green-500/10';
    case 'video':
    case 'audio':
      return 'text-primary bg-primary/10';
    case 'archive':
      return 'text-amber-600 bg-amber-600/10';
    default:
      return 'text-muted-foreground bg-muted';
  }
}

/**
 * Retorna classes de cor para badges (usado em DealAttachmentsSection)
 */
export function getFileBadgeColor(fileType: string): string {
  switch (fileType) {
    case 'image':
      return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    case 'pdf':
      return 'bg-red-500/10 text-red-500 border-red-500/20';
    case 'document':
      return 'bg-primary/10 text-primary border-primary/20';
    case 'spreadsheet':
      return 'bg-green-500/10 text-green-500 border-green-500/20';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

/**
 * Formata tamanho de arquivo em bytes para exibição legível
 */
/**
 * Converte mime_type string para tipo compatível com MediaThumbnail
 * Usado para mapear documentos do cliente e wizard para o componente de thumbnail
 */
export function getMimeMediaType(mimeType: string | null | undefined): string {
  if (!mimeType) return 'document';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'document';
}

/**
 * Formata tamanho de arquivo em bytes para exibição legível
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(size < 10 ? 1 : 0)} ${units[unitIndex]}`;
}
